/**
 * What the room is fetching and sorting, as lines a panel can render.
 *
 * Pure and on its own, like `filters.ts` and `personaWork.ts`: CRA's jest cannot
 * resolve react-router-dom, so anything a component imports is untestable if it
 * lives beside the component. The ORDER and the WORDS come from
 * `LOOKUP_STATE_OPTIONS`, not from a second list here — two lists of the same
 * five states is how "waiting to be read" ends up meaning different numbers in
 * two places.
 */
import { LOOKUP_STATE_OPTIONS } from './filters';
import type {
  BoardroomLookupCounts,
  BoardroomLookupStage,
  BoardroomLookupStages,
  BoardroomLookupState,
  BoardroomTagging,
} from '../../types';

export type LookupTone = 'busy' | 'waiting' | 'bad' | 'quiet';

export interface LookupLine {
  key: BoardroomLookupState;
  label: string;
  count: number;
  tone: LookupTone;
}

function toneOf(key: BoardroomLookupState, count: number): LookupTone {
  if (count === 0) return 'quiet';
  if (key === 'failed') return 'bad';
  if (key === 'landed') return 'waiting';
  if (key === 'read') return 'quiet';
  return 'busy';
}

/** The rows to show: everything with something in it, plus whatever is selected
 *  (a chosen row that vanished when its count hit zero left the feed filtered by
 *  something with no row to unclick). */
export function lookupLines(
  counts: BoardroomLookupCounts | null | undefined,
  active: BoardroomLookupState | null = null,
): LookupLine[] {
  const c = counts ?? ({} as BoardroomLookupCounts);
  return LOOKUP_STATE_OPTIONS.filter(
    o => (c[o.key] ?? 0) > 0 || active === o.key,
  ).map(o => ({
    key: o.key,
    label: o.label,
    count: c[o.key] ?? 0,
    tone: toneOf(o.key, c[o.key] ?? 0),
  }));
}

/** "2 fetching · 3 waiting to be read" — the one line a collapsed panel shows.
 *  Null when the room is not waiting on the network at all. */
export function lookupSummary(
  counts: BoardroomLookupCounts | null | undefined,
): string | null {
  const c = counts ?? ({} as BoardroomLookupCounts);
  const parts: string[] = [];
  const moving = (c.fetching ?? 0) + (c.queued ?? 0);
  if (moving > 0) parts.push(`${moving} fetching`);
  if ((c.landed ?? 0) > 0) parts.push(`${c.landed} waiting to be read`);
  if ((c.failed ?? 0) > 0) parts.push(`${c.failed} failed`);
  if ((c.not_fetched ?? 0) > 0) parts.push(`${c.not_fetched} not fetched`);
  return parts.length ? parts.join(' · ') : null;
}

/** Is the network doing anything for this room right now. */
export function isBusy(counts: BoardroomLookupCounts | null | undefined): boolean {
  const c = counts ?? ({} as BoardroomLookupCounts);
  return (c.fetching ?? 0) + (c.queued ?? 0) > 0;
}

// ----------------------------- sorting -----------------------------

export type TaggingTone = 'working' | 'waiting' | 'done' | 'parked';

/** "146 waiting to be sorted · 846 sorted: none has all 4 tags yet, 846
 *  have at least one the item does not answer". Null when there is nothing to say: a room with
 *  no axes yet has nothing to sort ONTO, and "0 sorted" there reads as a
 *  failure rather than as a room nobody has framed.
 *
 *  LOOKED AT IS NOT SORTED. This said "846 sorted" while TYPE was on 286 of
 *  the room's 992 things and no item had all four tags: a skip ("nothing on
 *  the board says") counted the same as a tag. The looked-at number is split
 *  into the two, so the numbers add up to what the Tags panel shows. */
export function taggingLine(t: BoardroomTagging | null | undefined): string | null {
  if (!t || !t.axes?.length) return null;
  const n = t.axes.length;
  const all = n === 1 ? 'its tag' : `all ${n} tags`; // "260 have its tag" with one axis
  const parts: string[] = [];
  if (t.waiting > 0) parts.push(`${t.waiting.toLocaleString()} waiting to be sorted`);
  if (t.sorted > 0) {
    const complete = t.complete ?? 0;
    const skipped = t.skipped ?? 0;
    // "SORTED", NOT "LOOKED AT": read as "1,108 looked at again", it sounded
    // like everything had been re-queued, when it meant the sorter was done.
    let looked = `${t.sorted.toLocaleString()} sorted`;
    if (complete + skipped === t.sorted) {
      looked += complete
        ? `: ${complete.toLocaleString()} ${complete === 1 ? 'has' : 'have'} ${all}`
        : `: none has ${all} yet`;
      if (skipped)
        looked += `, ${skipped.toLocaleString()} ${skipped === 1 ? 'has' : 'have'} at least one the item does not answer`;
    }
    parts.push(looked);
  }
  return parts.length ? parts.join(' · ') : 'nothing to sort yet';
}

/** "438k" - a token count the way the budget picker says it. */
export function tokensShort(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

/** The daily budget for sorting after digs, as a line: what is spent, and -
 *  when it is used up with items still waiting - that the rest waits for
 *  tomorrow. Null when there is nothing to show. */
export function budgetLine(t: BoardroomTagging | null | undefined): { text: string; stopped: boolean } | null {
  const b = t?.after_budget;
  if (!t || !b) return null;
  if (b.cap <= 0) return { text: 'Sorting after digs is off', stopped: t.waiting > 0 };
  // THE LAST 24 HOURS, which is what the budget counts - not since midnight.
  const spent = `${tokensShort(b.spent)} of ${tokensShort(b.cap)} spent on sorting after digs in the last 24 hours`;
  if (b.used_up && t.waiting > 0) {
    return { text: `The sorting budget for the last 24 hours is spent - the ${t.waiting.toLocaleString()} waiting are sorted during the next dig, or raise it`, stopped: true };
  }
  return { text: spent, stopped: false };
}

/** The budgets offered: off, and steps a person can reason about. */
export const SORTING_BUDGETS = [0, 250_000, 500_000, 1_000_000, 2_000_000, 5_000_000];

/** Why it is not sorting, in the room's words rather than the loop's. */
export function taggingNote(t: BoardroomTagging | null | undefined): string | null {
  if (!t) return null;
  const note = (t.note || '').toLowerCase();
  if (!note) return null;
  if (note.includes('credit')) return 'waiting for credits';
  if (note.includes('no axes')) return 'no tag groups yet - the room is not set up';
  // "all sorted" and "nothing placed" are states, not problems: the line above
  // already says the first and the second is a batch that found nothing.
  // "nothing fitted" likewise: the content or feature step had nothing to do
  // this round. Shown as "sorting stopped", it flickered on a busy sorter.
  if (note.includes('all sorted') || note.includes('nothing placed') || note.includes('nothing fitted')) return null;
  return 'sorting stopped - see the run';
}

export function taggingTone(t: BoardroomTagging | null | undefined): TaggingTone {
  if (!t) return 'done';
  if (t.working) return 'working';
  if (taggingNote(t)) return 'parked';
  return t.waiting > 0 ? 'waiting' : 'done';
}

// ----------------------------- stages -----------------------------

// In the order a lookup moves through them.
const STAGE_LABELS: [BoardroomLookupStage, string][] = [
  ['waiting_to_search', 'waiting to search'],
  ['searching', 'searching'],
  ['waiting_to_load', 'waiting to load page'],
  ['loading', 'loading page'],
  ['waiting_for_data', 'waiting for data'],
  ['getting_data', 'getting data'],
  ['parsing', 'parsing'],
  ['in_console', 'waiting for you in the console'],
];

/** "2 waiting to search · 1 loading page · 3 parsing", or "" when nothing is out. */
export function stagesLine(stages?: Partial<BoardroomLookupStages>): string {
  if (!stages) return '';
  return STAGE_LABELS.filter(([k]) => (stages[k] ?? 0) > 0)
    .map(([k, label]) => `${stages[k]} ${label}`)
    .join(' · ');
}
